import { useEffect, useState, useMemo, useCallback, useRef, memo } from "react";
import { useAuth } from "../context/AuthContext";
import { motion, AnimatePresence, config } from "framer-motion";
import { Container, Box, Typography, Grid, Card, CardContent, Avatar, Stack, IconButton, Tooltip, Tabs, Tab, ThemeProvider, createTheme, Chip, Button, DialogActions, DialogTitle, Dialog, DialogContent, CircularProgress, } from "@mui/material";
import { QuestionAnswer, Description, AddShoppingCart, Inventory2, CurrencyRupee, Schedule, PeopleAlt, TrendingUp, TrendingDown, Close, Apps as AppsIcon, LocalShipping, CalendarMonth, DateRange, Timeline, MoreVert, ShoppingCartCheckout, ShoppingCart, } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, LabelList, Tooltip as ReTooltip, PieChart, Pie, Cell, Rectangle, LineChart, Line, } from "recharts";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import { KPICardWithTrend, InfoCard, PremiumFloatingMenu, CLIENT_PIE_COLORS, PRODUCT_PIE_COLORS, EMPLOYEE_PIE_COLORS, UserActivityPopup } from "../components/DashboardItems";
import { useNavNotifications } from "../context/NotificationContext";
import { toProperCase } from "../utils/formatRows";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useDataLoadingState from "../hooks/useDataLoadingState";
import LoadingSpinner from "../components/Common/LoadingSpinner";

// const theme = createTheme({
//   typography: {
//     fontFamily: '"Calibri", "Aptos", "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
//     h6: { fontWeight: 700 },
//     body1: { fontWeight: 400 },
//     body2: { fontWeight: 400 },
//   },
//   breakpoints: {
//     values: { xs: 0, sm: 600, md: 1000, lg: 1200, xl: 1536 },
//   },
// });

const formatINR = (num) => {
  const n = Number(num) || 0;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const apiClient = axios.create({
  headers: {
    "Cache-Control": "no-store, no-cache, must-revalidate, proxy-revalidate",
    Pragma: "no-cache",
    Expires: "0",
  },
});

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const value = Number(payload[0]?.value ?? 0) || 0;
    return (
      <div style={{ background: "rgba(15,23,42,0.96)", borderRadius: 14, padding: "12px 16px", boxShadow: "0 16px 40px rgba(0,0,0,0.35)", border: "1px solid rgba(255,255,255,0.08)", backdropFilter: "blur(12px)", minWidth: 120 }} >
        <Typography fontWeight={700} fontSize={11} sx={{ color: "rgba(255,255,255,0.6)", textTransform: "uppercase", letterSpacing: "0.4px" }}> {label} </Typography>
        <Typography fontWeight={800} fontSize={16} sx={{ color: "#fff", mt: 0.3 }}>
          {(() => {
            const num = value;
            if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
            if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
            if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
            return formatINR(num);
          })()}
        </Typography>
      </div>
    );
  }
  return null;
};

const DTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
console.log("DTZ", DTZ);

const Home = ({ connectionString }) => {
  const { companyData, selectedCompany, user } = useAuth();
  const companyId = selectedCompany?.CompId ?? null;
  const userId = user?.UserId;
  console.log(userId);
  const API = `${connectionString}/api`;
  const navigate = useNavigate();

  const { notifications } = useNavNotifications();

  const [stats, setStats] = useState([]);
  const [topClientsByOrders, setTopClientsByOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [topSellingItems, setTopSellingItems] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [chartPeriod, setChartPeriod] = useState("yearly");
  const [loading, setLoading] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showRecentPopup, setShowRecentPopup] = useState(false);
  const [kpiPeriod, setKpiPeriod] = useState("monthly");
  const [clientPeriod, setClientPeriod] = useState("monthly");
  const [productPeriod, setProductPeriod] = useState("monthly");
  const [employeePeriod, setEmployeePeriod] = useState("monthly");
  const [clientLimit, setClientLimit] = useState(5);
  const [productLimit, setProductLimit] = useState(5);
  const [employeeLimit, setEmployeeLimit] = useState(5);
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 550);
  const [periodLoading, setPeriodLoading] = useState(false);
  const [totalSales, setTotalSales] = useState(0);
  const [outstandingAmount, setOutstandingAmount] = useState(0);
  const [overdueAmount, setOverdueAmount] = useState(0);

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [userActivities, setUserActivities] = useState([]);
  const [dialogActivities, setDialogActivities] = useState([]);
  const [dialogUserName, setDialogUserName] = useState("");

  const dialogDataRef = useRef(null);

  const latestActivityPerUser = useMemo(() => {
    const map = {};

    userActivities.forEach(item => {
      if (!item.addUserId) return;

      if (
        !map[item.addUserId] ||
        new Date(item.ist_datetime) > new Date(map[item.addUserId].ist_datetime)
      ) {
        map[item.addUserId] = item;
      }
    });

    return Object.values(map);
  }, [userActivities]);

  const activitySummaryPerUser = useMemo(() => {
    const map = {};

    userActivities.forEach(item => {
      if (!item.addUserId) return;

      if (!map[item.addUserId]) {
        map[item.addUserId] = {
          addUserId: item.addUserId,
          leadFrom: item.leadFrom,
          totalActivities: 0
        };
      }

      map[item.addUserId].totalActivities += 1;
    });

    return Object.values(map);
  }, [userActivities]);


  const handleOpenDialog = useCallback((notification) => {
    // Create a snapshot and store in ref
    const snapshot = userActivities.filter( act => act.addUserId === notification.userId );

    // Store snapshot in ref BEFORE setting state
    dialogDataRef.current = {
      activities: snapshot,
      userName: notification.userName
    };

    // Then set state from the ref
    setDialogActivities(snapshot);
    setDialogUserName(notification.userName);
    setOpenDialog(true);
  }, [userActivities]);

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Clear the ref after closing
    dialogDataRef.current = null;
  };

  const navigateToMap = {
    // "Active Inquiries": "/main/v4/inquiry-details",
    "Orders": "/main/v2/orderDetail-stepper",
    "Total Revenue": "/main/v2/sale-invoice",
    "Total Sales": "/main/v2/sale-invoice",
    "Outstanding": "/main/OSandOD-bills-report",
    "Overdue": "/main/OSandOD-bills-report",
    "Total Purchase": "/main/Purchase-InvoiceDetails",
    "Purchase Outstanding": "/main/OSandOD-bills-report",
    "Purchase Overdue": "/main/OSandOD-bills-report",
  };

  const getMonthlyFallback = () => [
    { month: "Week 1", sales: 0 },
    { month: "Week 2", sales: 0 },
    { month: "Week 3", sales: 0 },
    { month: "Week 4", sales: 0 },
  ];

  const getWeeklyFallback = () => {
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    return days.map((d) => ({ month: d, sales: 0 }));
  };

  const fetchChartData = useCallback(async (period) => {
    if (!companyId) return;

    try {
      // Add this mapping BEFORE the API call
      const periodMap = {
        "monthly": "monthly",
        "weekly": "weekly",
        "yearly": "yearly",
        "today": "today"
      };

      const res = await apiClient.get(`${API}/monthly-wise-Sale`, {
        params: {
          CompId: companyId,
          period: periodMap[period],  // ✅ Sends "month", "week", "year", "today"
          userId: userId
        }
      });

      let formattedData = [];
      if (period === "yearly") {
        const monthNames = ["Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar"];
        const monthOrder = [4, 5, 6, 7, 8, 9, 10, 11, 12, 1, 2, 3];
        const monthMap = {};
        (res.data.data || []).forEach(item => { 
          monthMap[item.MonthNumber] = Number(item.TotalSales) || 0; 
        });

        formattedData = monthOrder.map((m, i) => ({
          month: monthNames[i],
          sales: monthMap[m] || 0
        }));
      } else if (period === "monthly") {
        formattedData = (res.data.data || []).map((item) => ({
          month: item.WeekLabel || `Week ${item.WeekNumber}`,
          sales: Number(item.TotalSales) || 0,
        }));

        if (!formattedData.length) {
          formattedData = getMonthlyFallback();
        } else {
          const weeksMap = {};
          formattedData.forEach((w) => (weeksMap[String(w.month).trim()] = w.sales));

          formattedData = [];
          for (let i = 1; i <= 4; i++) {
            const label = `Week ${i}`;
            formattedData.push({ month: label, sales: Number(weeksMap[label] || 0) });
          }
        }
      } else if (period === "weekly") {
        formattedData = (res.data.data || []).map((item) => ({
          month: item.DayName ? item.DayName.slice(0, 3) : item.DayDate,
          sales: Number(item.TotalSales) || 0,
        }));

        if (!formattedData.length) {
          formattedData = getWeeklyFallback();
        } else {
          const order = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
          const map = {};
          formattedData.forEach((d) => (map[String(d.month).trim()] = d.sales));
          formattedData = order.map((d) => ({ month: d, sales: Number(map[d] || 0) }));
        }
      } else if (period === "today") {
        const now = new Date();
        const currentHour = now.getHours();

        const hourMap = {};
        (res.data.data || []).forEach((item) => {
          if (typeof item.HourNumber === "number") {
            hourMap[item.HourNumber] = Number(item.TotalSales) || 0;
          }
        });

        formattedData = [];
        for (let h = 0; h <= currentHour; h++) {
          formattedData.push({
            month: `${String(h).padStart(2, "0")}:00`,
            sales: hourMap[h] || 0,
          });
        }
      }

      let finalData = formattedData;

      if (isSmallScreen) {
        finalData = formattedData.filter(item => item.sales > 0);
      }

      if (finalData.length === 0 && isSmallScreen) {
        finalData = formattedData;
      }

      setChartData(finalData);
    } catch (err) {
      console.error("Chart data error:", err);
      if (period === "monthly") setChartData(getMonthlyFallback());
      else if (period === "weekly") setChartData(getWeeklyFallback());
      else setChartData(["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"].map((m) => ({ month: m, sales: 0 })));
    }
  }, [companyId, API, isSmallScreen, userId]);

  useEffect(() => {
    const handleResize = () => setIsSmallScreen(window.innerWidth < 550);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const saved = sessionStorage.getItem("kpiPeriod");
    if (saved && ["today", "weekly", "monthly", "yearly"].includes(saved)) {
      setKpiPeriod(saved);
    }
  }, []);

  useEffect(() => {
    sessionStorage.setItem("kpiPeriod", kpiPeriod);
  }, [kpiPeriod]);

  useEffect(() => {
    setChartPeriod(kpiPeriod);
  }, [kpiPeriod]);

  // ✅ MAIN EFFECT - Single orchestration, NO loading state changes during render
  useEffect(() => {
    if (!companyId) return;

    const groupBy = kpiPeriod === "today" ? "today" : kpiPeriod === "weekly" ? "week" : kpiPeriod === "monthly" ? "month" : "year";
    const groupByClient = clientPeriod === "today" ? "today" : clientPeriod === "weekly" ? "week" : clientPeriod === "monthly" ? "month" : "year";
    const groupByProduct = productPeriod === "today" ? "today" : productPeriod === "weekly" ? "week" : productPeriod === "monthly" ? "month" : "year";
    const groupByEmployee = employeePeriod === "today" ? "today" : employeePeriod === "weekly" ? "week" : employeePeriod === "monthly" ? "month" : "year";

    const fetchAllData = async () => {
      try {
        // Fetch all data in parallel with error handling
        const results = await Promise.allSettled([
          apiClient.get(`${API}/report/export?companyid=${companyId}&groupby=${groupBy}&userId=${userId}`),
          apiClient.get(`${API}/recent-activity?CompId=${companyId}`),
          apiClient.get(`${API}/user-daily-activity`, { params: { CompId: companyId, userId } }),
          apiClient.get(`${API}/getTopClients`, { params: { companyid: companyId, groupby: groupByClient, limit: clientLimit, userId: userId } }),
          apiClient.get(`${API}/top-products`, { params: { companyid: companyId, groupby: groupByProduct, limit: productLimit, userId: userId } }),
          apiClient.get(`${API}/top-employees`, { params: { companyid: companyId, groupby: groupByEmployee, limit: employeeLimit, userId: userId } }),
          apiClient.get(`${API}/total-sales?companyid=${companyId}&userId=${userId}&groupby=${groupBy}`),
          apiClient.get(`${API}/outstandingAmount?companyid=${companyId}&userId=${userId}&groupby=${groupBy}`),
          apiClient.get(`${API}/overdueAmount?companyid=${companyId}&userId=${userId}&groupby=${groupBy}`),
          apiClient.get(`${API}/total-purchase?companyid=${companyId}&userId=${userId}&groupby=${groupBy}`),
          apiClient.get(`${API}/outstanding-purchase?companyid=${companyId}&userId=${userId}&groupby=${groupBy}`),
          apiClient.get(`${API}/overdue-purchase?companyid=${companyId}&userId=${userId}&groupby=${groupBy}`),
        ]);

        const reportRes = results[0].status === 'fulfilled' ? results[0].value : { data: {} };
        const recentActRes = results[1].status === 'fulfilled' ? results[1].value : { data: {} };
        const userDailyActRes = results[2].status === 'fulfilled' ? results[2].value : { data: {} };
        const topClientsRes = results[3].status === 'fulfilled' ? results[3].value : { data: {} };
        const topProductsRes = results[4].status === 'fulfilled' ? results[4].value : { data: {} };
        const topEmployeesRes = results[5].status === 'fulfilled' ? results[5].value : { data: {} };
        const salesRes = results[6].status === 'fulfilled' ? results[6].value : { data: {} };
        const outstandingRes = results[7].status === 'fulfilled' ? results[7].value : { data: {} };
        const overdueRes = results[8].status === 'fulfilled' ? results[8].value : { data: {} };
        const purchaseRes = results[9].status === 'fulfilled' ? results[9].value : { data: {} };
        const outstandingPurchaseRes = results[10].status === 'fulfilled' ? results[10].value : { data: {} };
        const overduePurchaseRes = results[11].status === 'fulfilled' ? results[11].value : { data: {} };

        // Log if any API failed
        results.forEach((result, idx) => {
          if (result.status === 'rejected') {
            console.error(`API ${idx} failed:`, result.reason);
          }
        });

        // ✅ Fetch chart data WITHOUT awaiting - prevents blocking
        fetchChartData(kpiPeriod).catch(err => console.error("Chart error:", err));

        const reportData = reportRes.data || {};
        const summary = reportData.summary || {};
        const successfulInquiries = Array.isArray(reportData.successfulInquiries) ? reportData.successfulInquiries : [];
        const salesOrders = Array.isArray(reportData.salesOrders) ? reportData.salesOrders : [];
        const saleInvoices = Array.isArray(reportData.saleInvoices) ? reportData.saleInvoices : [];

        let activeInquiriesValueRaw = Number(summary.offer?.value) || 0;
        let ordersValueRaw = Number(summary.salesOrder?.value) || 0;
        let totalRevenueValueRaw = Number(summary.invoice?.value) || 0;

        if (kpiPeriod === "today") {
          const lastInq = successfulInquiries[successfulInquiries.length - 1];
          const lastOrder = salesOrders[salesOrders.length - 1];
          const lastInvoice = saleInvoices[saleInvoices.length - 1];

          if (lastInq) activeInquiriesValueRaw = Number(lastInq.totalOfferValue) || 0;
          if (lastOrder) ordersValueRaw = Number(lastOrder.totalRevenue) || 0;
          if (lastInvoice) totalRevenueValueRaw = Number(lastInvoice.totalTaxableAmount || lastInvoice.totalBilledAmount) || 0;
        }

        const inquiriesPct = { pct: Math.abs(summary.offer?.growth || 0).toFixed(2), trend: summary.offer?.trend === "up" ? "up" : summary.offer?.trend === "down" ? "down" : "same" };
        const ordersPct = { pct: Math.abs(summary.salesOrder?.growth || 0).toFixed(2), trend: summary.salesOrder?.trend === "up" ? "up" : summary.salesOrder?.trend === "down" ? "down" : "same" };
        const invoicesPct = { pct: Math.abs(summary.invoice?.growth || 0).toFixed(2), trend: summary.invoice?.trend === "up" ? "up" : summary.invoice?.trend === "down" ? "down" : "same" };

        const formatSparkData = (arr, valueKey) => {
          return (arr || []).map(item => {
            const rawValue = item[valueKey] || 0;
            const fullValue = Number(rawValue);
            const period = item.period || "N/A";

            let label = "N/A";
            if (period.includes("-W")) {
              label = period.replace("2025-W", "Week ").replace(/-\d+$/, "");
            } else if (period.includes("-")) {
              try {
                label = new Date(period).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
              } catch (e) {
                label = period;
              }
            } else {
              label = period;
            }

            return { v: fullValue / 10000, value: fullValue, label: label };
          });
        };

        const sparkSuccessfulInquiries = formatSparkData(successfulInquiries, "totalOfferValue");
        const sparkSalesOrders = formatSparkData(salesOrders, "totalRevenue");
        const sparkSaleInvoices = formatSparkData(saleInvoices, "totalTaxableAmount");

        setRecentActivities(recentActRes.data?.data || []);
        setUserActivities(userDailyActRes.data?.data || []);

        const topClientsPayload = topClientsRes?.data?.topClients ?? topClientsRes?.data?.data ?? topClientsRes?.data ?? [];
        const normalizedTopClients = (Array.isArray(topClientsPayload) ? topClientsPayload : []).map((item) => ({
          ClientName: item.clientName ?? item.ClientName ?? item.name ?? "Unknown",
          TotalOrders: Number(item.invoiceCount ?? item.totalOrders ?? item.orders ?? 0),
          TotalRevenue: Number(item.totalBilledAmount ?? item.totalRevenue ?? 0),
          TotalInvoices: Number(item.invoiceCount ?? 0),
          TotalSales: Number(item.totalBilledAmount ?? item.totalSales ?? 0),
          TotalQuantity: Number(item.totalQuantity ?? 0),
          percentageOfTotalRevenue: Number(item.percentageOfTotalRevenue ?? 0),
        }));

        const productsPayload = topProductsRes?.data?.topProducts ?? topProductsRes?.data?.data ?? topProductsRes?.data ?? [];
        const normalizedTopProducts = (Array.isArray(productsPayload) ? productsPayload : []).map((item) => ({
          ProductName: item.productName ?? item.ProductName ?? item.ItemName ?? item.itemName ?? "Unknown",
          TotalInvoices: Number(item.totalQuantity ?? 0),
          TotalSales: Number(item.totalRevenue ?? 0),
          itemCode: item.itemCode ?? "Unknown",
          percentageOfTotalRevenue: Number(item.percentageOfTotalRevenue ?? 0),
        }));

        const employeesPayload = topEmployeesRes?.data?.topEmployees ?? topEmployeesRes?.data?.data ?? topEmployeesRes?.data ?? [];
        const normalizedTopEmployees = (Array.isArray(employeesPayload) ? employeesPayload : []).map((item) => ({
          ItemName: item.employeeName ?? item.EmployeeName ?? item.staffName ?? item.StaffName ?? "Unknown",
          TotalQtySold: Number(item.invoiceCount ?? 0),
          TotalSalesValue: Number(item.totalBilledAmount ?? 0),
          percentageOfTotal: Number(item.percentageOfTotal ?? 0),
        }));

        // Extract sales metrics
        const totalSalesValue = salesRes?.data?.data?.totalSales || 0;
        const totalSalesGrowth = salesRes?.data?.data?.growth || 0;
        const totalSalesTrend = salesRes?.data?.data?.trend || "same";

        const outstandingValue = outstandingRes?.data?.data?.outstandingAmount || 0;
        const outstandingGrowth = outstandingRes?.data?.data?.growth || 0;
        const outstandingTrend = outstandingRes?.data?.data?.trend || "same";

        const overdueValue = overdueRes?.data?.data?.overdueAmount || 0;
        const overdueGrowth = overdueRes?.data?.data?.growth || 0;
        const overdueTrend = overdueRes?.data?.data?.trend || "same";

        const totalPurchaseValue = purchaseRes?.data?.data?.totalPurchase || 0;
        const totalPurchaseGrowth = purchaseRes?.data?.data?.growth || 0;
        const totalPurchaseTrend = purchaseRes?.data?.data?.trend || "same";

        const outstandingPurchaseValue = outstandingPurchaseRes?.data?.data?.outstandingPurchaseAmount || 0;
        const outstandingPurchaseGrowth = outstandingPurchaseRes?.data?.data?.growth || 0;
        const outstandingPurchaseTrend = outstandingPurchaseRes?.data?.data?.trend || "same";

        const overduePurchaseValue = overduePurchaseRes?.data?.data?.overduePurchaseAmount || 0;
        const overduePurchaseGrowth = overduePurchaseRes?.data?.data?.growth || 0;
        const overduePurchaseTrend = overduePurchaseRes?.data?.data?.trend || "same";

        setTotalSales(totalSalesValue);
        setOutstandingAmount(outstandingValue);
        setOverdueAmount(overdueValue);

        // ✅ Update all state at once WITHOUT changing loading state
        // "Billing" plan -> hide Total Purchase / Purchase Outstanding / Purchase
        // Overdue. "Accounting" plan (or any other/unknown plan) -> show every card.
        const isBillingPlan = selectedCompany?.planName === "Billing";
        setStats([
          // { title: "Active Inquiries", value: formatINR(activeInquiriesValueRaw), change: `${inquiriesPct.pct}%`, trend: inquiriesPct.trend, iconImage: "/Funnel2.svg", color: "#8b5cf6", sparkData: sparkSuccessfulInquiries },
          // ❌ COMMENTED: Orders Card
          // { title: "Orders", value: formatINR(ordersValueRaw), change: `${ordersPct.pct}%`, trend: ordersPct.trend, iconImage: "/growth.svg", color: "#3b82f6", sparkData: sparkSalesOrders },
          // ❌ COMMENTED: Total Revenue Card
          // { title: "Total Revenue", value: formatINR(totalRevenueValueRaw), change: `${invoicesPct.pct}%`, trend: invoicesPct.trend, iconImage: "/Revenue2.svg", color: "#10b981", sparkData: sparkSaleInvoices },
          { title: "Total Sales", value: formatINR(totalSalesValue), change: `${totalSalesGrowth}%`, trend: totalSalesTrend, iconImage: "/Revenue2.svg", color: "#3b82f6" },
          { title: "Outstanding", value: formatINR(outstandingValue), change: `${outstandingGrowth}%`, trend: outstandingTrend, iconImage: "/Revenue2.svg", color: "#f59e0b" },
          { title: "Overdue", value: formatINR(overdueValue), change: `${overdueGrowth}%`, trend: overdueTrend, iconImage: "/Revenue2.svg", color: "#ef4444" },
          ...(isBillingPlan ? [] : [
            { title: "Total Purchase", value: formatINR(totalPurchaseValue), change: `${totalPurchaseGrowth}%`, trend: totalPurchaseTrend, iconImage: "/Revenue2.svg", color: "#8b5cf6" },
            { title: "Purchase Outstanding", value: formatINR(outstandingPurchaseValue), change: `${outstandingPurchaseGrowth}%`, trend: outstandingPurchaseTrend, iconImage: "/Revenue2.svg", color: "#ec4899" },
            { title: "Purchase Overdue", value: formatINR(overduePurchaseValue), change: `${overduePurchaseGrowth}%`, trend: overduePurchaseTrend, iconImage: "/Revenue2.svg", color: "#dc2626" },
          ]),
        ]);

        setTopClientsByOrders(normalizedTopClients);
        setTopProducts(normalizedTopProducts);
        setTopSellingItems(normalizedTopEmployees);

        // ✅ CRITICAL: Set dataLoaded instead of loading to prevent framer-motion unmounting
        setDataLoaded(true);
        setLoading(false);
        setPeriodLoading(false);
      } catch (err) {
        console.error("Dashboard fetch error:", err);
        setDataLoaded(true);
        setLoading(false);
        setPeriodLoading(false);
      }
    };

    fetchAllData();
  }, [companyId, API, kpiPeriod, clientPeriod, productPeriod, employeePeriod, clientLimit, productLimit, employeeLimit, fetchChartData]);

  const clientChartData = topClientsByOrders.slice(0, clientLimit).map((c) => ({
    name: c.ClientName || "Unknown",
    value: c.TotalRevenue || 0,
    percentage: Number(c.percentageOfTotalRevenue) || 0,
  }));

  const productChartData = topProducts.slice(0, productLimit).map((p) => ({
    name: p.ProductName || "Unknown",
    value: p.TotalSales || 0,
    percentage: Number(p.percentageOfTotalRevenue) || 0,
  }));

  const employeeChartData = topSellingItems.slice(0, employeeLimit).map((e) => ({
    name: e.ItemName || "Unknown",
    value: e.TotalSalesValue || 0,
    percentage: Number(e.percentageOfTotal) || 0,
  }));

  if (!companyData || companyData.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ py: 12, textAlign: "center" }}>
        <Typography variant="h5" fontWeight={700} color="text.secondary">
          Welcome! Please complete company registration to view dashboard.
        </Typography>
      </Container>
    );
  }

  return (
    // <ThemeProvider theme={theme}>
    <Box sx={{ minHeight: "100vh", bgcolor: "#F3F4F6" }}>
      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 } }}>

        {/* ✅ ONLY render when dataLoaded is true */}
        {dataLoaded && (
          <>
            <Grid container spacing={1} mb={{ xs: 1, md: 2 }}>
              <Grid item size={{ xs: 12 }}>
                <Box sx={{ display: "flex", justifyContent: { xs: "center", md: "flex-start" }, position: "relative" }}>
                  {/* backdropFilter blur removed here — a large always-on
                      blur area is one of the most expensive things to
                      rasterize every frame on low-end/integrated GPUs. A
                      flat semi-opaque background gives the same look
                      without the per-frame blur cost. */}
                  <Stack direction="row" spacing={1.5} sx={{ pt: 0.5, borderRadius: 5, background: "rgba(255,255,255,0.7)", border: "1px solid rgba(255,255,255,0.4)", flexWrap: "wrap", justifyContent: 'center' }}>
                    {["today", "weekly", "monthly", "yearly"].map((p) => {
                      const isActive = kpiPeriod === p;
                      const isLoading = periodLoading && isActive;
                      return (
                        <motion.div key={p} whileTap={{ scale: 0.94 }}>
                          <Button
                            variant="text"
                            onClick={() => setKpiPeriod(p)}
                            disabled={isLoading}
                            sx={{
                              minWidth: { xs: "auto", sm: 110 },
                              borderRadius: 4,
                              textTransform: "none",
                              fontWeight: 500,
                              fontSize: "0.9rem",
                              bgcolor: isActive ? "#E2E8F0" : "transparent",
                              color: isActive ? "#000000" : "#64748b",
                              border: isActive ? "2px solid #E2E8F0" : "1px solid rgba(100,100,100,0.15)",
                              boxShadow: isActive ? "0 4px 12px rgba(0,0,0,0.12)" : "none",
                              position: "relative",
                              // Named properties instead of "all" — animating
                              // "all" makes the browser watch every CSS
                              // property (including layout-affecting ones)
                              // every frame, which is unnecessary cost on
                              // weak GPUs/CPUs when only color/background/
                              // shadow actually change here.
                              transition: "background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease",
                              "&:hover": {
                                bgcolor: isActive ? "#d1d9e0" : "#f8fafc",
                              },
                              "&:disabled": {
                                opacity: 1,
                              }
                            }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, justifyContent: "center" }}>
                              {isLoading && <CircularProgress size={16} sx={{ color: "#000000" }} />}
                              <span>{p === "today" ? "Today" : p.charAt(0).toUpperCase() + p.slice(1)}</span>
                            </Box>
                          </Button>
                        </motion.div>
                      );
                    })}
                  </Stack>

                  {/* Loading Overlay */}
                  <AnimatePresence>
                    {periodLoading && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        style={{
                          position: "fixed",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "rgba(248, 250, 252, 0.92)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          zIndex: 1000,
                        }}
                      >
                        <motion.div
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          exit={{ scale: 0.9, opacity: 0 }}
                          transition={{ type: "spring", stiffness: 300, damping: 25 }}
                        >
                          <Box
                            sx={{
                              background: "white",
                              borderRadius: 3,
                              p: 4,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              gap: 2,
                              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <CircularProgress size={48} sx={{ color: "#3b82f6" }} />
                            <Typography variant="h6" fontWeight={600} color="#1e293b">
                              Loading Data
                            </Typography>
                            <Typography variant="body2" color="#64748b" textAlign="center">
                              Fetching your dashboard metrics...
                            </Typography>
                          </Box>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Box>
              </Grid>

              {stats.map((stat, index) => (
                <Grid item size={{ xs: 6, sm: 4, md: 2 }} key={`kpi-${index}`}>
                  <KPICardWithTrend iconImage={stat.iconImage} title={stat.title} value={stat.value} color={stat.color} trend={stat.trend} onClick={() => navigate(navigateToMap[stat.title])} sparkData={stat.sparkData} highlight={stat.title === "Total Sales"} subtitle={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.3, fontSize: { xs: "0.70rem", sm: "0.75rem" }, fontWeight: 600, lineHeight: 1, ml: -1, flexWrap: "nowrap" }}>
                      {stat.trend === "up" ? (
                        <ArrowDropUpIcon sx={{ color: "#10b981", fontSize: 22 }} />
                      ) : stat.trend === "down" ? (
                        <ArrowDropDownIcon sx={{ color: "#ef4444", fontSize: 22 }} />
                      ) : (
                        <RemoveIcon sx={{ color: "#94a3b8", fontSize: 22 }} />
                      )}
                      <span>
                        {stat.change} vs{" "}
                        <Box component="span" sx={{ color: "#64748b", fontWeight: 400, fontSize: "0.78rem" }}>
                          {kpiPeriod === "today" ? "yesterday" : kpiPeriod === "weekly" ? "last week" : kpiPeriod === "monthly" ? "last month" : "last year"}
                        </Box>
                      </span>
                    </Box>
                  }
                  />
                </Grid>
              ))}
            </Grid>

            <Box mb={{ xs: 1, md: 2 }} >
              <div>
                {/* <Grid container spacing={3}>
                    <Grid item size={{ xs: 12 }}>
                      <Card sx={{ borderRadius: 4, p: { xs: 1.5, sm: 2.5 }, boxShadow: "0 10px 30px rgba(0,0,0,0.1)", background: "white" }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                          <Typography variant="h6" fontWeight={800} color="#1e293b"> Sales Performance </Typography>
                        </Box>

                        <ResponsiveContainer width="100%" height={250}>
                          <BarChart data={chartData} margin={{ top: 15, right: 30, left: 20, bottom: 5 }}>
                            <defs>
                              <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor="#00A1BC" stopOpacity={0.9} />
                                <stop offset="33%" stopColor="#034EA2" stopOpacity={0.8} />
                                <stop offset="100%" stopColor="#9D248F" stopOpacity={0.7} />
                              </linearGradient>
                            </defs>

                            <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" />
                            <XAxis dataKey="month" tick={{ fill: "#64748b", fontWeight: 500, fontSize: 13 }} />
                            <YAxis
                              tickFormatter={(value) => {
                                const num = Number(value);
                                if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
                                if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
                                if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
                                return `₹${num.toFixed(0)}`;
                              }}
                              tick={{ fill: "#64748b", fontSize: 12 }}
                            />
                            <ReTooltip content={<CustomTooltip />} />
                            <Bar dataKey="sales" fill="url(#salesGradient)" radius={[10, 10, 0, 0]} maxBarSize={30}>
                              <LabelList dataKey="sales" position="top"
                                formatter={(value) => {
                                  const num = Number(value);
                                  if (num === 0) return "";
                                  if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
                                  if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
                                  return formatINR(num);
                                }}
                                style={{ fill: "#1e293b", fontWeight: 500, fontSize: 12 }}
                              />
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </Card>
                    </Grid>
                  </Grid> */}
                <Grid container spacing={1.5} size={{ xs: 12 }}>

                  {/* ================= LEFT : SALES GRAPH ================= */}
                  {/* <Grid item size={{ xs: 12, md: 6 }}>
                    <Card sx={{ borderRadius: 4, p: { xs: 1.5, sm: 2.5 }, boxShadow: "0 10px 30px rgba(0,0,0,0.1)", background: "white" }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Typography variant="h6" fontWeight={800} color="#1e293b"> Sales Performance </Typography>
                      </Box>

                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={chartData} margin={{ top: 15, right: 30, left: 20, bottom: 5 }}>
                          <defs>
                            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#00A1BC" stopOpacity={0.9} />
                              <stop offset="33%" stopColor="#034EA2" stopOpacity={0.8} />
                              <stop offset="100%" stopColor="#9D248F" stopOpacity={0.7} />
                            </linearGradient>
                          </defs>

                          <CartesianGrid strokeDasharray="4 6" stroke="#e2e8f0" />
                          <XAxis dataKey="month" tick={{ fill: "#64748b", fontWeight: 500, fontSize: 13 }} />
                          <YAxis
                            tickFormatter={(value) => {
                              const num = Number(value);
                              if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
                              if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
                              if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
                              return `₹${num.toFixed(0)}`;
                            }}
                            tick={{ fill: "#64748b", fontSize: 12 }}
                          />
                          <ReTooltip content={<CustomTooltip />} />
                          <Bar dataKey="sales" fill="url(#salesGradient)" radius={[10, 10, 0, 0]} maxBarSize={30}>
                            <LabelList dataKey="sales" position="top"
                              formatter={(value) => {
                                const num = Number(value);
                                if (num === 0) return "";
                                if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
                                if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
                                return formatINR(num);
                              }}
                              style={{ fill: "#1e293b", fontWeight: 500, fontSize: 12 }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid> */}

                  {/* ================= LEFT : SALES GRAPH ================= */}
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <Card elevation={0} sx={{
                      borderRadius: "18px", border: "1px solid #EEF1F6", p: { xs: 2, sm: 2.75 },
                      boxShadow: "0 10px 28px rgba(15,23,42,0.06)", background: "linear-gradient(160deg, #FFFFFF 0%, #F7FAFF 100%)",
                      position: "relative", overflow: "hidden", height: 322, display: "flex", flexDirection: "column",
                    }}>
                      {/* Header — title + live period-total chip (derived
                          from the same chartData already computed above;
                          adds no new fetch/state). */}
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 0.5, position: "relative" }}>
                        <Box>
                          <Typography sx={{ fontWeight: 800, fontSize: "1rem", color: "#0F172A" }}>Sales Performance</Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#94A3B8", mt: "1px", textTransform: "capitalize" }}>{kpiPeriod} trend</Typography>
                        </Box>
                        <Box sx={{
                          px: 1.4, py: 0.6, borderRadius: "10px",
                          background: "linear-gradient(135deg, #1877F2 0%, #0855A8 100%)",
                          boxShadow: "0 6px 16px rgba(24,119,242,0.35)",
                        }}>
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.75)", textTransform: "uppercase", letterSpacing: "0.4px", lineHeight: 1 }}>Total</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#fff", lineHeight: 1.3 }}>
                            {(() => {
                              const total = chartData.reduce((s, d) => s + (Number(d.sales) || 0), 0);
                              if (total >= 10000000) return `₹${(total / 10000000).toFixed(2)}Cr`;
                              if (total >= 100000) return `₹${(total / 100000).toFixed(2)}L`;
                              if (total >= 1000) return `₹${(total / 1000).toFixed(1)}K`;
                              return formatINR(total);
                            })()}
                          </Typography>
                        </Box>
                      </Box>

                      <ResponsiveContainer width="100%" height="100%" style={{ flex: 1 }}>
                        <BarChart data={chartData} margin={{ top: 20, right: 12, left: 4, bottom: 5 }}>
                          <defs>
                            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#3B9EFF" stopOpacity={1} />
                              <stop offset="45%" stopColor="#1877F2" stopOpacity={0.95} />
                              <stop offset="100%" stopColor="#0A4A98" stopOpacity={0.9} />
                            </linearGradient>
                            <linearGradient id="salesGradientHover" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#6BB6FF" stopOpacity={1} />
                              <stop offset="45%" stopColor="#2E8AFC" stopOpacity={0.98} />
                              <stop offset="100%" stopColor="#0855A8" stopOpacity={0.95} />
                            </linearGradient>
                          </defs>

                          <CartesianGrid strokeDasharray="3 8" stroke="#EEF1F6" vertical={false} />
                          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: "#94A3B8", fontWeight: 600, fontSize: 12 }} />
                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tickFormatter={(value) => {
                              const num = Number(value);
                              if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
                              if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
                              if (num >= 1000) return `₹${(num / 1000).toFixed(1)}K`;
                              return `₹${num.toFixed(0)}`;
                            }}
                            tick={{ fill: "#94A3B8", fontSize: 11 }}
                          />
                          <ReTooltip content={<CustomTooltip />} cursor={{ fill: "rgba(24,119,242,0.06)", radius: 10 }} />
                          {/* animationDuration trimmed from recharts' ~1500ms
                              default — this bar chart re-animates on every
                              period switch, and a long grow-in animation on
                              every tap is a recurring cost, not a one-time
                              one, so it's worth keeping short. */}
                          <Bar dataKey="sales" radius={[10, 10, 10, 10]} maxBarSize={34} activeBar={{ fill: "url(#salesGradientHover)" }} animationDuration={400} animationEasing="ease-out">
                            {chartData.map((_, idx) => (
                              <Cell key={`bar-cell-${idx}`} fill="url(#salesGradient)" />
                            ))}
                            <LabelList dataKey="sales" position="top"
                              formatter={(value) => {
                                const num = Number(value);
                                if (num === 0) return "";
                                if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)}Cr`;
                                if (num >= 100000) return `₹${(num / 100000).toFixed(2)}L`;
                                return formatINR(num);
                              }}
                              style={{ fill: "#334155", fontWeight: 700, fontSize: 11.5 }}
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Card>
                  </Grid>

                  {/* ================= RIGHT : NOTIFICATIONS ================= */}
                  <Grid item size={{ xs: 12, md: 6 }}>
                    <Card
                      elevation={0}
                      sx={{
                        borderRadius: "14px",
                        border: "1px solid #F3F4F6",
                        p: { xs: 1.5, sm: 2.5 },
                        boxShadow: "0 2px 6px rgba(15,23,42,0.05)",
                        background: "white",
                        height: 322,
                        display: "flex",
                        flexDirection: "column"
                      }}
                    >
                      {/* ===== Header ===== */}
                      <Box sx={{ mb: 1.5 }}>
                        <Typography variant="h6" fontWeight={700} color="#111827" fontSize="1rem">
                          Today's Activity
                        </Typography>
                        <Typography fontSize={12} color="#6B7280">
                          Actions performed by you & your team
                        </Typography>
                      </Box>

                      {/* ===== TABLE CONTENT ===== */}
                      <Box
                        sx={{
                          flex: 1,
                          overflowY: "auto",
                          overflowX: "hidden",
                          "&::-webkit-scrollbar": { width: 6 },
                          "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "#cbd5e1",
                            borderRadius: 10
                          }
                        }}
                      >
                        {userActivities.length === 0 ? (
                          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                            No activity recorded today
                          </Typography>
                        ) : (
                          <Box>
                            {/* TABLE HEADER */}
                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1.6fr 1fr 0.4fr",
                                gap: 2,
                                px: 1.5,
                                py: 1,
                                alignItems: "center",
                                borderBottom: "2px solid #e2e8f0",
                                backgroundColor: "#f8fafc",
                                top: 0,
                                zIndex: 10,
                                mb: 0.5,
                                position: "sticky",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px"
                                }}
                              >
                                Users
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px"
                                }}
                              >
                                Activity
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  fontWeight: 700,
                                  color: "#64748b",
                                  textTransform: "uppercase",
                                  letterSpacing: "0.5px",
                                  textAlign: "center"
                                }}
                              >
                                Actions
                              </Typography>
                            </Box>

                            {/* TABLE ROWS */}
                            <Stack spacing={0}>
                              {activitySummaryPerUser.map((item, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1.6fr 1fr 0.4fr",
                                    gap: 2,
                                    px: 1.5,
                                    py: 1.2,
                                    alignItems: "center",
                                    borderBottom: "1px solid #e2e8f0",
                                    backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                                    // Named property + no box-shadow hover
                                    // transition — same reasoning as the
                                    // Activity Timeline rows: "all" and
                                    // animated box-shadow are the two most
                                    // expensive things to keep re-triggering
                                    // per row in a list on a weak device.
                                    transition: "background-color 0.2s ease",
                                    "&:hover": {
                                      backgroundColor: "#f1f5f9",
                                    }
                                  }}
                                >
                                  {/* COLUMN 1: USER */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                    <Avatar
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: "#e0e7ff",
                                        color: "#4f46e5",
                                        fontSize: "0.85rem",
                                        fontWeight: 700,
                                        flexShrink: 0
                                      }}
                                    >
                                      {(item.leadFrom || "U").charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 700,
                                          fontSize: "13px",
                                          color: "#0f172a",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis"
                                        }}
                                      >
                                        {item.leadFrom || "Unknown"}
                                      </Typography>
                                      {/* <Typography
                                        sx={{
                                          fontSize: "11px",
                                          color: "#64748b",
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis"
                                        }}
                                      >
                                        {item.message ? item.message.substring(0, 20) : "N/A"}
                                      </Typography> */}
                                    </Box>
                                  </Box>

                                  {/* COLUMN 2: ACTIVITY TYPE */}
                                  <Box sx={{ display: "flex", alignItems: "center", minWidth: 0 }}>
                                    <Chip
                                      label={`${item.totalActivities}`}
                                      size="small"
                                      sx={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        height: "auto",
                                        py: 0.4,
                                        bgcolor: "#e0f2fe",
                                        color: "#0369a1",
                                        border: "none",
                                        minWidth: 0
                                      }}
                                    />
                                  </Box>

                                  {/* COLUMN 5: ACTIONS */}
                                  <Box sx={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
                                    <IconButton
                                      size="small"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenDialog({
                                          userId: item.addUserId,
                                          userName: item.leadFrom
                                        });
                                      }}
                                      sx={{
                                        color: "#64748b",
                                        p: 0.5,
                                        "&:hover": {
                                          bgcolor: "#e2e8f0",
                                          color: "#1e293b"
                                        }
                                      }}
                                    >
                                      <MoreVertIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                </Grid>
              </div>
            </Box>

            <Grid container spacing={{ xs: 1, md: 1.5 }}>
              <Grid item size={{ xs: 12, md: 4 }} key="clients-card">
                <InfoCard title="Top Clients" icon={PeopleAlt} chartData={clientChartData} period={clientPeriod} colors={CLIENT_PIE_COLORS} limit={clientLimit} onPeriodChange={setClientPeriod} onLimitChange={setClientLimit}>
                  {topClientsByOrders.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 10, color: "#94a3b8" }}>
                      <Typography variant="h6" fontWeight={600}>No Clients</Typography>
                      <Typography variant="body2" mt={1}>No clients in this period.</Typography>
                    </Box>
                  ) : (
                    topClientsByOrders.slice(0, clientLimit).map((c, i) => (
                      <Box key={`client-${i}`} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: i < clientLimit - 1 ? "1px dashed #dbeafe" : "none" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, minWidth: 0, gap: { xs: 1, sm: 1 } }} >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ width: 36, height: 36, bgcolor: CLIENT_PIE_COLORS[i % CLIENT_PIE_COLORS.length], color: "white", fontSize: "0.9rem" }}>{i + 1}</Avatar>
                            <Box>
                              <Typography fontSize="1.05rem" color="#000" fontWeight={600} sx={{ maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} > {c.ClientName} </Typography>
                              <Typography variant="body2" fontWeight={400} fontSize="1rem" color="#000" sx={{ mt: 0.5 }}> {c.TotalOrders} invoices </Typography>
                            </Box>
                          </Stack>
                        </Box>
                        <Typography fontWeight={600} color="#059669" sx={{ fontSize: { xs: "0.85rem", sm: "0.92rem", md: "0.98rem" }, minWidth: "fit-content", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0, mt: 1.3 }} > {formatINR(c.TotalRevenue)} </Typography>
                      </Box>
                    ))
                  )}
                </InfoCard>
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }} key="products-card">
                <InfoCard title="Top Products" icon={ShoppingCart} chartData={productChartData} colors={PRODUCT_PIE_COLORS} period={productPeriod} limit={productLimit} onPeriodChange={setProductPeriod} onLimitChange={setProductLimit}>
                  {topProducts.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
                      <Typography variant="h6" fontWeight={600}>No Products</Typography>
                      <Typography variant="body2" mt={1}>No products in this period.</Typography>
                    </Box>
                  ) : (
                    topProducts.slice(0, productLimit).map((p, i) => (
                      <Box key={`product-${i}`} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: i < productLimit - 1 ? "1px dashed #dcfce7" : "none" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, minWidth: 0, gap: { xs: 1, sm: 1 } }} >
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ width: 36, height: 36, bgcolor: PRODUCT_PIE_COLORS[i % PRODUCT_PIE_COLORS.length], color: "white", fontSize: "0.9rem" }}>{i + 1}</Avatar>
                            <Box>
                              <Typography fontWeight={600} fontSize="1.05rem" color="#000" sx={{ maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}> {p.ProductName} </Typography>
                              <Typography variant="body2" color="#000" fontSize="0.8rem" fontWeight={400}>{p.itemCode}</Typography>
                              <Typography variant="body2" color="#000" fontSize="0.8rem" fontWeight={400}>{p.TotalInvoices} qty</Typography>
                            </Box>
                          </Stack>
                        </Box>
                        <Typography fontWeight={600} color="#059669" sx={{ fontSize: { xs: "0.85rem", sm: "0.92rem", md: "0.98rem" }, minWidth: "fit-content", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0 }} >{formatINR(p.TotalSales)}</Typography>
                      </Box>
                    ))
                  )}
                </InfoCard>
              </Grid>

              <Grid item size={{ xs: 12, md: 4 }} key="employees-card">
                <InfoCard title="Top Employees" icon={TrendingUp} chartData={employeeChartData} colors={EMPLOYEE_PIE_COLORS} period={employeePeriod} limit={employeeLimit} onPeriodChange={setEmployeePeriod} onLimitChange={setEmployeeLimit}>
                  {topSellingItems.length === 0 ? (
                    <Box sx={{ textAlign: "center", py: 8, color: "#94a3b8" }}>
                      <Typography variant="h6" fontWeight={600}>No Employees</Typography>
                      <Typography variant="body2" mt={1}>No data in this period.</Typography>
                    </Box>
                  ) : (
                    topSellingItems.slice(0, employeeLimit).map((e, i) => (
                      <Box key={`employee-${i}`} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, borderBottom: i < employeeLimit - 1 ? "1px dashed #dcfce7" : "none" }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1, minWidth: 0, gap: { xs: 1, sm: 1 } }} >

                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ width: 36, height: 36, bgcolor: EMPLOYEE_PIE_COLORS[i % EMPLOYEE_PIE_COLORS.length], color: "white", fontSize: "0.9rem" }}>{i + 1}</Avatar>
                            <Box>
                              <Typography fontSize="1.05rem" color="#000" fontWeight={600} sx={{ maxWidth: 180, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} >{e.ItemName}</Typography>
                              <Typography variant="body2" fontWeight={400} fontSize="1rem" color="#000">{e.TotalQtySold} invoices</Typography>
                            </Box>
                          </Stack>
                        </Box>
                        <Typography fontWeight={600} color="#059669" sx={{ fontSize: { xs: "0.85rem", sm: "0.92rem", md: "0.98rem" }, minWidth: "fit-content", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flexShrink: 0 }} >{formatINR(e.TotalSalesValue)}</Typography>
                      </Box>
                    ))
                  )}
                </InfoCard>
              </Grid>
            </Grid>
          </>
        )}
      </Container>

      <UserActivityPopup
        open={openDialog}
        onClose={handleCloseDialog}
        userName={dialogUserName}
        activities={dialogActivities}
      />

    </Box>
    // </ThemeProvider>
  );
};

export default Home;